import { useState } from "react";
import { Flame, RefreshCw, Sparkles } from "lucide-react";
import { useBoard, useCompleteQuest, useRerollQuest, useStreak, useSummary } from "../lib/api";
import { fmtXp } from "../lib/xp";
import type { BoardEntry } from "../lib/types";
import { Button, Chip, Empty, Sheet, effortTone, fmtDate } from "../components/ui";
import { Tonight } from "../components/Tonight";

export function Today({ onLog, userId }: { onLog: () => void; userId: string }) {
  const board = useBoard();
  const streak = useStreak();
  const summary = useSummary(2);
  const today = summary.data?.find((d) => d.local_date === board.data?.date);
  const [open, setOpen] = useState<BoardEntry | null>(null);

  return (
    <div className="grid gap-4">
      <header className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-ink-mute">
            {board.data ? fmtDate(board.data.date) : "Today"}
          </p>
          <h1 className="text-3xl font-semibold tracking-tight">Today</h1>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-ink-soft">
          <Flame size={16} className={streak.data?.current ? "text-warn" : "text-ink-mute"} />
          <span className="numeral">{streak.data?.current ?? 0}</span>
          <span className="text-ink-mute">day streak</span>
        </div>
      </header>

      <section className="grid grid-cols-3 gap-2">
        <Stat
          label="Readiness"
          value={today?.readiness ?? "—"}
          tone={readinessTone(today?.readiness)}
        />
        <Stat
          label="Sleep"
          value={today?.sleep_s ? (today.sleep_s / 3600).toFixed(1) + "h" : "—"}
        />
        <Stat label="XP today" value={fmtXp(today?.xp_total ?? 0)} tone="accent" />
      </section>

      {board.data && (
        <Tonight board={board.data} userId={userId} sleepToday={today?.sleep_s ?? null} />
      )}

      <section className="grid gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Dailies</h2>
          {board.data && (
            <span className="text-xs text-ink-mute">
              {board.data.rerolls_left} reroll{board.data.rerolls_left === 1 ? "" : "s"} left this
              week
            </span>
          )}
        </div>
        {board.isLoading && <Empty>Rolling today's quests…</Empty>}
        {board.error && <p className="text-sm text-danger">{(board.error as Error).message}</p>}
        {board.data?.dailies.map((entry) => (
          <QuestCard key={entry.instance.id} entry={entry} onOpen={() => setOpen(entry)} />
        ))}
        {board.data && board.data.dailies.length === 0 && (
          <Empty>No quests yet — pull to refresh after 04:00.</Empty>
        )}
      </section>

      {board.data?.slayer && (
        <section className="grid gap-3">
          <h2 className="text-lg font-semibold">Assignment</h2>
          <QuestCard entry={board.data.slayer} onOpen={() => setOpen(board.data!.slayer!)} />
        </section>
      )}

      <Button className="mt-2 w-full py-3" onClick={onLog}>
        Log a session
      </Button>

      <QuestSheet
        entry={open}
        onClose={() => setOpen(null)}
        rerollsLeft={board.data?.rerolls_left ?? 0}
      />
    </div>
  );
}

function readinessTone(r: number | null | undefined): "accent" | "recovery" | "warn" | "mute" {
  if (r == null) return "mute";
  if (r < 60) return "warn";
  if (r >= 80) return "recovery";
  return "accent";
}

function Stat({
  label,
  value,
  tone = "mute",
}: {
  label: string;
  value: string | number;
  tone?: "accent" | "recovery" | "warn" | "mute";
}) {
  const color = {
    accent: "text-accent",
    recovery: "text-recovery",
    warn: "text-warn",
    mute: "text-ink",
  }[tone];
  return (
    <div className="card p-3">
      <p className="text-xs text-ink-mute">{label}</p>
      <p className={`numeral mt-0.5 text-xl ${color}`}>{value}</p>
    </div>
  );
}

function AwardsLine({ entry }: { entry: BoardEntry }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1">
      {Object.entries(entry.quest.awards).map(([skill, xp]) => (
        <span
          key={skill}
          className={`numeral text-xs ${skill === "recovery" ? "text-recovery" : "text-accent"}`}
        >
          +{fmtXp(xp as number)} {skill}
        </span>
      ))}
      {entry.quest.bonus && (
        <span className="text-xs text-ink-mute">
          ×{entry.quest.bonus.multiplier} bonus possible
        </span>
      )}
    </div>
  );
}

function QuestCard({ entry, onOpen }: { entry: BoardEntry; onOpen: () => void }) {
  const done = entry.instance.status === "completed";
  return (
    <button
      onClick={onOpen}
      className={`tap card w-full p-4 text-left transition active:scale-[0.99] ${done ? "opacity-60" : ""} ${entry.instance.generated_by === "llm" ? "card-glow" : ""}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className={`text-base font-semibold ${done ? "line-through" : ""}`}>
            {entry.quest.name}
          </h3>
          <p className="mt-0.5 line-clamp-2 text-sm text-ink-soft">{entry.quest.blurb}</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Chip tone={effortTone(entry.quest.effort)}>{entry.quest.effort}</Chip>
          {entry.instance.generated_by === "llm" && (
            <Chip tone="accent">
              <Sparkles size={11} className="mr-1 inline" />
              PT
            </Chip>
          )}
        </div>
      </div>
      <div className="mt-3">
        <AwardsLine entry={entry} />
      </div>
    </button>
  );
}

function QuestSheet({
  entry,
  onClose,
  rerollsLeft,
}: {
  entry: BoardEntry | null;
  onClose: () => void;
  rerollsLeft: number;
}) {
  const complete = useCompleteQuest();
  const reroll = useRerollQuest();
  if (!entry) return null;
  const auto = entry.quest.completion !== null;
  const offered = entry.instance.status === "offered";
  return (
    <Sheet open onClose={onClose} title={entry.quest.name}>
      <div className="grid gap-4">
        <div className="flex flex-wrap gap-2">
          <Chip tone={effortTone(entry.quest.effort)}>{entry.quest.effort}</Chip>
          {entry.quest.rest_eligible && <Chip tone="recovery">counts as rest</Chip>}
          {entry.instance.kind === "slayer" && <Chip>assignment</Chip>}
          {auto && <Chip>auto-completes from your log</Chip>}
        </div>
        <p className="text-ink-soft">{entry.quest.blurb}</p>
        {entry.instance.rationale && (
          <div className="rounded-chip bg-surface-2 p-3 text-sm text-ink-soft">
            <p className="mb-1 text-xs uppercase tracking-widest text-ink-mute">
              Why the PT set this
            </p>
            {entry.instance.rationale}
          </div>
        )}
        <AwardsLine entry={entry} />
        {complete.error && (
          <p className="text-sm text-danger">{(complete.error as Error).message}</p>
        )}
        {reroll.error && <p className="text-sm text-danger">{(reroll.error as Error).message}</p>}
        {offered ? (
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Button
              disabled={complete.isPending}
              onClick={() => {
                complete.mutate(entry.instance.id, { onSuccess: onClose });
              }}
            >
              {auto ? "Mark done anyway" : "Complete"}
            </Button>
            {entry.instance.kind === "daily" && (
              <Button
                variant="ghost"
                disabled={rerollsLeft < 1 || reroll.isPending}
                onClick={() => reroll.mutate(entry.instance.id, { onSuccess: onClose })}
              >
                <RefreshCw size={16} />
              </Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-ink-mute">This quest is {entry.instance.status}.</p>
        )}
      </div>
    </Sheet>
  );
}
